import React from 'react';

// Built-in schematics for the structural figures — what the screen is made of
// and where things sit. These render until a real screenshot with the matching
// name lands in src/Assets/manual/, and they stay useful either way because
// they label the parts a screenshot can only show.
//
// Everything is drawn with CSS custom properties so the diagrams follow the
// app's theme instead of hard-coding a palette.

const INK    = 'var(--mn-ink, #0f172a)';
const MUTED  = 'var(--mn-muted, #64748b)';
const LINE   = 'var(--mn-line, #cbd5e1)';
const FILL   = 'var(--mn-fill, #f8fafc)';
const ACCENT = 'var(--mn-accent, #4338ca)';
const WARN   = 'var(--mn-warn, #f59e0b)';

function Frame({ children, w = 720, h = 300, label }) {
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="mn-svg" role="img" aria-label={label}>
      <rect x="1" y="1" width={w - 2} height={h - 2} rx="10"
        fill={FILL} stroke={LINE} strokeWidth="1.5" />
      {children}
    </svg>
  );
}

function Label({ x, y, children, size = 11, weight = 600, fill = MUTED, anchor = 'start' }) {
  return (
    <text x={x} y={y} fontSize={size} fontWeight={weight} fill={fill}
      textAnchor={anchor} fontFamily="inherit">{children}</text>
  );
}

// Numbered callout badge
function Pin({ x, y, n }) {
  return (
    <g>
      <circle cx={x} cy={y} r="9" fill={ACCENT} />
      <text x={x} y={y + 3.5} fontSize="10" fontWeight="800" fill="#fff"
        textAnchor="middle" fontFamily="inherit">{n}</text>
    </g>
  );
}

// ── 01 — the global header ────────────────────────────────────────────────────
function GlobalHeaderDiagram() {
  const tabs = ['Flow', 'Schedule', 'Road', 'Money', 'Customers'];
  return (
    <Frame w={720} h={150} label="The REKON top bar">
      <rect x="16" y="20" width="688" height="52" rx="8" fill="#fff" stroke={LINE} />
      <circle cx="44" cy="46" r="11" fill={ACCENT} />
      <Label x={62} y={50} size={13} weight={800} fill={INK}>REKON</Label>

      {tabs.map((t, i) => {
        const x = 140 + i * 84;
        const active = i === 0;
        return (
          <g key={t}>
            {active && <rect x={x - 8} y="32" width="76" height="28" rx="6" fill={ACCENT} opacity="0.12" />}
            <Label x={x} y={50} size={12} weight={active ? 800 : 600} fill={active ? ACCENT : MUTED}>{t}</Label>
            {t === 'Money' && <circle cx={x + 46} cy="38" r="7" fill={WARN} />}
            {t === 'Money' && <text x={x + 46} y="41.5" fontSize="9" fontWeight="800" fill="#fff" textAnchor="middle" fontFamily="inherit">6</text>}
          </g>
        );
      })}

      <rect x="566" y="32" width="72" height="28" rx="6" fill={FILL} stroke={LINE} />
      <Label x={576} y={50} size={10}>Search</Label>
      <circle cx="660" cy="46" r="12" fill={FILL} stroke={LINE} />
      <circle cx="690" cy="46" r="12" fill={FILL} stroke={LINE} />

      <Pin x={30} y={104} n={1} />
      <Label x={46} y={108}>Five workspaces — the whole product</Label>
      <Pin x={272} y={104} n={2} />
      <Label x={288} y={108}>Badge = jobs with money stuck</Label>
      <Pin x={520} y={104} n={3} />
      <Label x={536} y={108}>Gear → Settings</Label>
    </Frame>
  );
}

// ── 02 — the flow board ───────────────────────────────────────────────────────
function FlowBoardDiagram() {
  const cols = ['Intake', 'Diag', 'Approval', 'Parts', 'Repair', 'Verify', 'Ready'];
  const counts = [2, 3, 1, 2, 3, 1, 2];
  return (
    <Frame w={720} h={300} label="The Flow board">
      {cols.map((c, i) => {
        const x = 16 + i * 100;
        return (
          <g key={c}>
            <rect x={x} y="16" width="92" height="240" rx="8" fill="#fff" stroke={LINE} />
            <Label x={x + 10} y={36} size={11} weight={800} fill={INK}>{c}</Label>
            <Label x={x + 82} y={36} size={10} anchor="end">{counts[i]}</Label>
            {Array.from({ length: counts[i] }).map((_, j) => (
              <g key={j}>
                <rect x={x + 8} y={48 + j * 46} width="76" height="38" rx="5"
                  fill={FILL} stroke={i === 3 ? WARN : LINE} strokeWidth={i === 3 ? 1.8 : 1} />
                <rect x={x + 14} y={56 + j * 46} width="34" height="5" rx="2.5" fill={MUTED} opacity="0.55" />
                <rect x={x + 14} y={66 + j * 46} width="52" height="4" rx="2" fill={MUTED} opacity="0.3" />
                <rect x={x + 14} y={74 + j * 46} width="26" height="4" rx="2" fill={MUTED} opacity="0.3" />
              </g>
            ))}
          </g>
        );
      })}
      <path d={`M 24 274 L 690 274`} stroke={ACCENT} strokeWidth="1.5" markerEnd="url(#mn-arrow)" opacity="0.5" />
      <defs>
        <marker id="mn-arrow" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
          <path d="M0,0 L8,4 L0,8 Z" fill={ACCENT} opacity="0.5" />
        </marker>
      </defs>
      <Label x={24} y={290} size={10}>Work moves left to right. A job sits in exactly one column.</Label>
      <Label x={696} y={290} size={10} anchor="end" fill={WARN}>Amber = sitting too long</Label>
    </Frame>
  );
}

// ── 03 — a job card ───────────────────────────────────────────────────────────
function JobCardDiagram() {
  return (
    <Frame w={580} h={230} label="Anatomy of a job card">
      <rect x="24" y="24" width="230" height="150" rx="8" fill="#fff" stroke={WARN} strokeWidth="2" />
      <Label x={40} y={48} size={13} weight={800} fill={INK}>RO 1042</Label>
      <rect x={186} y={36} width="52" height="16" rx="8" fill={WARN} opacity="0.18" />
      <Label x={212} y={47} size={9} weight={800} fill="#92400e" anchor="middle">4h idle</Label>
      <Label x={40} y={70} size={11} weight={700} fill={INK}>Dimmick Transport</Label>
      <Label x={40} y={88} size={10}>2019 Peterbilt 579 · Unit 318</Label>
      <line x1="40" y1="102" x2="238" y2="102" stroke={LINE} />
      <circle cx="46" cy="120" r="4" fill={WARN} />
      <Label x={58} y={124} size={10} fill="#92400e" weight={700}>Waiting on parts</Label>
      <rect x="40" y="138" width="22" height="22" rx="11" fill={ACCENT} opacity="0.15" />
      <Label x={51} y={153} size={9} weight={800} fill={ACCENT} anchor="middle">JD</Label>
      <Label x={70} y={153} size={10}>$1,025.59</Label>

      <Pin x={276} y={44} n={1} /><Label x={292} y={48}>RO number and how long it has sat</Label>
      <Pin x={276} y={78} n={2} /><Label x={292} y={82}>Customer and unit</Label>
      <Pin x={276} y={120} n={3} /><Label x={292} y={124}>Blocker, if there is one</Label>
      <Pin x={276} y={150} n={4} /><Label x={292} y={154}>Assigned tech and value</Label>
      <Label x={24} y={200} size={10}>You are scanning for colour, not reading every card.</Label>
    </Frame>
  );
}

// ── 04 — the job page ─────────────────────────────────────────────────────────
function JobPageDiagram() {
  const stops = ['Intake', 'Diag', 'Estimate', 'Approval', 'Repair', 'QC', 'Invoice', 'Paid'];
  return (
    <Frame w={720} h={320} label="The job page">
      {stops.map((s, i) => {
        const x = 40 + i * 84;
        const done = i <= 4;
        return (
          <g key={s}>
            {i > 0 && <line x1={x - 68} y1="40" x2={x - 12} y2="40" stroke={done ? ACCENT : LINE} strokeWidth="2" />}
            <circle cx={x} cy="40" r="9" fill={done ? ACCENT : '#fff'} stroke={done ? ACCENT : LINE} strokeWidth="2" />
            {i === 4 && <circle cx={x} cy="40" r="14" fill="none" stroke={ACCENT} strokeWidth="1.5" opacity="0.4" />}
            <Label x={x} y={64} size={9} anchor="middle" fill={done ? INK : MUTED}>{s}</Label>
          </g>
        );
      })}

      <rect x="16" y="86" width="430" height="200" rx="8" fill="#fff" stroke={LINE} />
      {['Activity', 'Findings', 'Photos', 'Unit', 'Customer'].map((t, i) => (
        <g key={t}>
          {i === 0 && <rect x={26 + i * 82} y="96" width="72" height="22" rx="5" fill={ACCENT} opacity="0.12" />}
          <Label x={30 + i * 82} y={111} size={9.5} weight={i === 0 ? 800 : 600} fill={i === 0 ? ACCENT : MUTED}>{t}</Label>
        </g>
      ))}
      {[0, 1, 2, 3].map(i => (
        <g key={i}>
          <circle cx="38" cy={142 + i * 34} r="4" fill={MUTED} opacity="0.4" />
          <rect x="52" y={134 + i * 34} width={280 - i * 30} height="5" rx="2.5" fill={MUTED} opacity="0.45" />
          <rect x="52" y={146 + i * 34} width={180 - i * 20} height="4" rx="2" fill={MUTED} opacity="0.25" />
        </g>
      ))}

      <rect x="458" y="86" width="246" height="94" rx="8" fill="#fff" stroke={LINE} />
      <Label x={470} y={106} size={10} weight={800} fill={INK}>PARTS</Label>
      {[0, 1, 2].map(i => (
        <rect key={i} x="470" y={118 + i * 18} width={210 - i * 40} height="5" rx="2.5" fill={MUTED} opacity="0.35" />
      ))}

      <rect x="458" y="192" width="246" height="94" rx="8" fill="#fff" stroke={ACCENT} strokeWidth="1.5" />
      <Label x={470} y={212} size={10} weight={800} fill={ACCENT}>MONEY</Label>
      <Label x={470} y={234} size={16} weight={800} fill={INK}>$1,025.59</Label>
      <Label x={470} y={252} size={10}>Approved · Net 30</Label>
      <rect x="470" y="262" width="100" height="16" rx="8" fill={ACCENT} />
      <Label x={520} y={273} size={9} weight={800} fill="#fff" anchor="middle">Generate Invoice</Label>

      <Pin x={22} y={18} n={1} />
      <Label x={16} y={306} size={10}>1 Lifecycle rail · 2 The job’s story and details · 3 Parts · 4 The money, and the next action</Label>
      <Pin x={430} y={100} n={2} /><Pin x={690} y={100} n={3} /><Pin x={690} y={206} n={4} />
    </Frame>
  );
}

// ── 05 — the estimate builder ─────────────────────────────────────────────────
function EstimateBuilderDiagram() {
  return (
    <Frame w={720} h={300} label="The estimate builder">
      <rect x="16" y="16" width="466" height="268" rx="8" fill="#fff" stroke={LINE} />

      <rect x="30" y="30" width="438" height="112" rx="6" fill={FILL} stroke={LINE} />
      <Label x={42} y={50} size={11} weight={800} fill={INK}>OPERATION 1 — Air Leak</Label>
      {['COMPLAINT', 'CAUSE', 'CORRECTION'].map((c, i) => (
        <g key={c}>
          <Label x={42} y={70 + i * 22} size={8} weight={800} fill={ACCENT}>{c}</Label>
          <rect x={116} y={64 + i * 22} width={320 - i * 40} height="5" rx="2.5" fill={MUTED} opacity="0.35" />
        </g>
      ))}
      <Label x={42} y={134} size={9}>Labor 3.5 hr × $145 · Parts (2) · Taxable</Label>

      <rect x="30" y="152" width="438" height="60" rx="6" fill={FILL} stroke={LINE} />
      <Label x={42} y={172} size={11} weight={800} fill={INK}>OPERATION 2 — Annual DOT Inspection</Label>
      <rect x={332} y={162} width="72" height="15" rx="7.5" fill={WARN} opacity="0.2" />
      <Label x={368} y={173} size={8} weight={800} fill="#92400e" anchor="middle">TAX EXEMPT</Label>
      <Label x={42} y={194} size={9}>Flat $120 · Parts (1)</Label>

      <rect x="30" y="224" width="130" height="26" rx="6" fill="#fff" stroke={ACCENT} strokeDasharray="4 3" />
      <Label x={95} y={241} size={10} weight={700} fill={ACCENT} anchor="middle">+ Add Operation</Label>

      <rect x="494" y="16" width="210" height="268" rx="8" fill="#fff" stroke={LINE} />
      <Label x={508} y={38} size={10} weight={800} fill={INK}>TOTALS</Label>
      {[['Labor', '$627.50'], ['Parts', '$174.00'], ['Shop Supplies', '$25.38'], ['Subtotal', '$826.88'], ['Tax (8%)', '$54.15']].map(([k, v], i) => (
        <g key={k}>
          <Label x={508} y={62 + i * 20} size={10}>{k}</Label>
          <Label x={690} y={62 + i * 20} size={10} anchor="end" fill={INK}>{v}</Label>
        </g>
      ))}
      <line x1="508" y1="172" x2="690" y2="172" stroke={LINE} />
      <Label x={508} y={192} size={12} weight={800} fill={INK}>TOTAL</Label>
      <Label x={690} y={192} size={13} weight={800} fill={INK} anchor="end">$856.03</Label>
      <rect x="508" y="212" width="182" height="28" rx="6" fill={ACCENT} />
      <Label x={599} y={230} size={10} weight={800} fill="#fff" anchor="middle">Send for Approval</Label>
      <Label x={508} y={262} size={9}>Preview shows the customer’s copy</Label>
    </Frame>
  );
}

// ── 12 — the money queues ─────────────────────────────────────────────────────
function MoneyQueuesDiagram() {
  const queues = [
    { label: 'Unapproved', n: 3, sub: 'Waiting on the customer', act: 'Resend / call' },
    { label: 'Unbilled',   n: 2, sub: 'Done, never invoiced',    act: 'Generate invoice' },
    { label: 'Unpaid',     n: 6, sub: 'Invoiced, balance open',  act: 'Record payment' },
  ];
  return (
    <Frame w={720} h={250} label="The three money queues">
      {queues.map((q, i) => {
        const x = 24 + i * 228;
        return (
          <g key={q.label}>
            <rect x={x} y="24" width="212" height="150" rx="8" fill="#fff" stroke={LINE} />
            <Label x={x + 16} y={48} size={12} weight={800} fill={INK}>{q.label}</Label>
            <circle cx={x + 190} cy="43" r="11" fill={ACCENT} />
            <text x={x + 190} y="47" fontSize="11" fontWeight="800" fill="#fff" textAnchor="middle" fontFamily="inherit">{q.n}</text>
            <Label x={x + 16} y={68} size={10}>{q.sub}</Label>
            {[0, 1, 2].map(j => (
              <g key={j}>
                <rect x={x + 16} y={84 + j * 22} width="120" height="5" rx="2.5" fill={MUTED} opacity="0.35" />
                <rect x={x + 148} y={84 + j * 22} width="48" height="5" rx="2.5" fill={MUTED} opacity="0.2" />
              </g>
            ))}
            <rect x={x + 16} y="146" width="110" height="18" rx="9" fill={ACCENT} opacity="0.12" />
            <Label x={x + 71} y={158} size={9} weight={800} fill={ACCENT} anchor="middle">{q.act}</Label>
            {i < 2 && <path d={`M ${x + 218} 99 l 12 0`} stroke={LINE} strokeWidth="2" />}
          </g>
        );
      })}
      <Label x={24} y={206} size={11} weight={700} fill={INK}>Each tab is a work queue, not a report.</Label>
      <Label x={24} y={226} size={10}>Unbilled catches finished jobs with no paperwork at all — the money that used to walk out the door.</Label>
    </Frame>
  );
}

export const DIAGRAMS = {
  '01-global-header':   GlobalHeaderDiagram,
  '02-flow-board':      FlowBoardDiagram,
  '03-job-card':        JobCardDiagram,
  '04-job-page':        JobPageDiagram,
  '05-estimate-builder': EstimateBuilderDiagram,
  '12-money-queues':    MoneyQueuesDiagram,
};
